import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { useTranslation } from '../hooks/useTranslation';
import api from '../services/api';
import { useToast } from '../components/ui/use-toast';
import { 
  Webhook, Plus, Trash2, Play, Copy, CheckCircle, XCircle, 
  Settings, ExternalLink, Slack, MessageCircle, Globe, Eye, EyeOff,
  RefreshCw, Loader2, ArrowLeft
} from 'lucide-react';

const WebhooksPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});

  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: [],
    integration_type: 'custom',
    is_active: true
  });

  useEffect(() => {
    loadWebhooks();
    loadEvents();
  }, []);

  const loadWebhooks = async () => {
    try {
      const response = await api.get('/webhooks');
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      console.error('Failed to load webhooks', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await api.get('/webhooks/events');
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to load events', error);
    }
  };

  const handleCreateWebhook = async () => {
    if (!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs requis',
        variant: 'destructive',
      });
      return;
    }

    try {
      await api.post('/webhooks', newWebhook);
      toast({
        title: 'Succès',
        description: 'Webhook créé avec succès',
      });
      setDialogOpen(false);
      setNewWebhook({ name: '', url: '', events: [], integration_type: 'custom', is_active: true });
      loadWebhooks();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Échec de la création',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce webhook ?')) return;

    try {
      await api.delete(`/webhooks/${webhookId}`);
      toast({
        title: 'Succès',
        description: 'Webhook supprimé',
      });
      loadWebhooks();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Échec de la suppression',
        variant: 'destructive',
      });
    }
  };

  const handleTestWebhook = async (webhookId) => {
    setTestingWebhook(webhookId);
    try {
      await api.post(`/webhooks/${webhookId}/test`);
      toast({
        title: 'Test envoyé',
        description: 'Le webhook de test a été envoyé',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Échec du test',
        variant: 'destructive',
      });
    } finally {
      setTestingWebhook(null);
    }
  };

  const handleToggleWebhook = async (webhookId, isActive) => {
    try {
      await api.put(`/webhooks/${webhookId}`, { is_active: isActive });
      loadWebhooks();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Échec de la mise à jour',
        variant: 'destructive',
      });
    }
  };

  const toggleEventSelection = (eventId) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copié',
      description: 'Secret copié dans le presse-papier',
    });
  };

  const getIntegrationIcon = (type) => {
    switch (type) {
      case 'slack':
        return <Slack className="h-5 w-5 text-[#4A154B]" />;
      case 'discord':
        return <MessageCircle className="h-5 w-5 text-[#5865F2]" />;
      default:
        return <Globe className="h-5 w-5 text-blue-600" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="webhooks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Webhooks</h1>
            <p className="text-slate-600 mt-1">Recevez des notifications dans vos systèmes externes</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Créer un Webhook</DialogTitle>
              <DialogDescription>
                Configurez un nouveau webhook pour recevoir des notifications
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="webhook_name">Nom du webhook *</Label>
                <Input
                  id="webhook_name"
                  value={newWebhook.name}
                  onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Mon webhook Slack"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="integration_type">Type d'intégration</Label>
                <Select
                  value={newWebhook.integration_type}
                  onValueChange={(v) => setNewWebhook(prev => ({ ...prev, integration_type: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">
                      <div className="flex items-center space-x-2">
                        <Globe className="h-4 w-4" />
                        <span>URL personnalisée</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="slack">
                      <div className="flex items-center space-x-2">
                        <Slack className="h-4 w-4" />
                        <span>Slack</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="discord">
                      <div className="flex items-center space-x-2">
                        <MessageCircle className="h-4 w-4" />
                        <span>Discord</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="webhook_url">URL du webhook *</Label>
                <Input
                  id="webhook_url"
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                  placeholder={
                    newWebhook.integration_type === 'slack' 
                      ? 'https://hooks.slack.com/services/...'
                      : newWebhook.integration_type === 'discord'
                        ? 'https://discord.com/api/webhooks/...'
                        : 'https://your-server.com/webhook'
                  }
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="mb-2 block">Événements à recevoir *</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                  {events.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-start space-x-3 p-2 rounded hover:bg-white cursor-pointer"
                      onClick={() => toggleEventSelection(event.id)}
                    >
                      <Checkbox
                        checked={newWebhook.events.includes(event.id)}
                        onCheckedChange={() => toggleEventSelection(event.id)}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{event.name}</p>
                        <p className="text-xs text-slate-500">{event.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreateWebhook}
                className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white"
              >
                Créer le webhook
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Webhooks List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 mx-auto animate-spin text-blue-600" />
          <p className="text-slate-600 mt-2">Chargement...</p>
        </div>
      ) : webhooks.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="text-center py-12">
            <Webhook className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun webhook configuré</h3>
            <p className="text-slate-600 mb-4">
              Créez votre premier webhook pour recevoir des notifications dans Slack, Discord ou votre propre système.
            </p>
            <Button onClick={() => setDialogOpen(true)} className="bg-gradient-to-r from-blue-600 to-violet-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Créer un webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.id} className="border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-slate-100 rounded-lg">
                      {getIntegrationIcon(webhook.integration_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-slate-900">{webhook.name}</h3>
                        <Badge variant={webhook.is_active ? "default" : "secondary"} className={webhook.is_active ? "bg-green-100 text-green-700" : ""}>
                          {webhook.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-1 font-mono truncate max-w-md">{webhook.url}</p>
                      
                      {/* Events */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {webhook.events.map((event) => (
                          <Badge key={event} variant="outline" className="text-xs">
                            {event}
                          </Badge>
                        ))}
                      </div>

                      {/* Secret */}
                      <div className="flex items-center space-x-2 mt-3">
                        <span className="text-xs text-slate-500">Secret:</span>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">
                          {showSecrets[webhook.id] ? webhook.secret : '••••••••••••••••'}
                        </code>
                        <button
                          onClick={() => setShowSecrets(prev => ({ ...prev, [webhook.id]: !prev[webhook.id] }))}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          {showSecrets[webhook.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </button>
                        <button
                          onClick={() => copyToClipboard(webhook.secret)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center space-x-4 mt-3 text-xs text-slate-500">
                        <span className="flex items-center space-x-1">
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          <span>{webhook.success_count} succès</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <XCircle className="h-3 w-3 text-red-500" />
                          <span>{webhook.failure_count} échecs</span>
                        </span>
                        {webhook.last_triggered && (
                          <span>
                            Dernier appel: {new Date(webhook.last_triggered).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={webhook.is_active}
                      onCheckedChange={(checked) => handleToggleWebhook(webhook.id, checked)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestWebhook(webhook.id)}
                      disabled={testingWebhook === webhook.id}
                    >
                      {testingWebhook === webhook.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default WebhooksPage;
